// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Link, { LinkProps } from "@insite/mobius/Link";
import Select, { SelectProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { initializeScrollTo } from "@mcmc/App/Libraries";
import { screen } from "@mcmc/XDL/Helpers";
import mergeToNew from "@mcmc/XDL/Utilities/xMergeToNew";
import React, { FC, useState } from "react";
import { css } from "styled-components";

type Props = {
    alphabet: LetterDetails[];
    prefix?: string;
    extendedStyles?: AlphabetNavigationStyles;
    /* Not on the design 
    onLetterClick?: (letter: string) => void;
    */
};

export interface LetterDetails {
    letter: string;
    linkable: boolean;
}

export interface AlphabetNavigationStyles {
    container?: InjectableCss;
    mobileContainer?: InjectableCss;
    navigationItem?: InjectableCss;
    navigationLink?: LinkProps;
    navigationLinkText?: TypographyPresentationProps;
    navigationTextEmpty?: TypographyPresentationProps;
    navigationSelect?: SelectProps;
}

const navigationTypographyStyle: TypographyPresentationProps = {
    size: 18,
    lineHeight: "20px",
    forwardAs: "span",
    transform: "uppercase",
    color: "secondary",
    css: css`
        vertical-align: middle;
    `,
};

export const alphabetNavigationStyles: AlphabetNavigationStyles = {
    container: {
        css: css`
            padding: 15px;
            margin-left: -9px;
            span:last-child {
                &::after {
                    content: none;
                }
            }
            ${screen("lte", "sm")`
                display: none;
            `}
        `,
    },
    mobileContainer: {
        css: css`
            padding: 15px;
            flex: 0 0 50%;
            width: 100%;
            ${screen("gt", "sm")`
                display: none;
            `}
        `,
    },
    navigationItem: {
        css: css`
            display: inline-block;
            position: relative;
            padding: 0 9px;

            &::after {
                content: "";
                position: absolute;
                border-right: 1px solid ${getColor("common.border")};
                top: 2px;
                bottom: 2px;
                right: 0;
            }
        `,
    },
    navigationLink: {
        color: "secondary",
        css: css`
            ${getProp("theme.link.defaultProps.css")}
            display: block;
        `,
        typographyProps: navigationTypographyStyle,
    },
    navigationTextEmpty: {
        ...navigationTypographyStyle,
        transform: "capitalize",
        color: "text",
    },
    navigationSelect: {
        cssOverrides: {
            inputSelect: css`
                ${getProp("theme.formField.defaultProps.cssOverrides.inputSelect")}
                text-transform: capitalize;
            `,
        },
    },
};

const StyledSpan = getStyledWrapper("span");

const AlphabetNavigation: FC<Props> = (props: Props) => {
    const [styles] = useState(() => mergeToNew(alphabetNavigationStyles, props.extendedStyles));
    const { alphabet } = props;

    /* Not on the design
    const handleClickOfLetter = (letter: string) => {
        props.onLetterClick?.(letter);
    }; */

    const handleClickOfLetter = (letter: string) => {
        const letterElementStr = `#${props.prefix || ""}letter-${letter === "#" ? "0-9" : letter}`;
        initializeScrollTo(letterElementStr);
    };

    // XNG-Change
    // const createHandleClickOfLetter = (letter: string) => () => {
    //     const alphabetElement = document.getElementById("alphabetNavigation");
    //     const letterElement = document.getElementById(`${props.prefix || ""}letter-${letter}`);
    //     if (alphabetElement && letterElement) {
    //         const alphabetOffset = alphabetElement.offsetTop;
    //         const letterOffset = letterElement.offsetTop;
    //         if (letterOffset - alphabetOffset > 0) {
    //             window.scrollTo(0, letterOffset - alphabetOffset);
    //         }
    //     }
    //     return handleClickOfLetter(letter);
    // };

    if (alphabet.length <= 0) {
        return null;
    }

    return (
        <>
            <StyledWrapper {...styles.container} data-test-selector="alphabetNavigation">
                <StyledSpan {...styles.navigationItem}>
                    <Typography {...styles.navigationTextEmpty}>{translate("All")}</Typography>
                </StyledSpan>
                {alphabet.map(alphabetItem => (
                    <StyledSpan
                        {...styles.navigationItem}
                        key={alphabetItem.letter}
                        data-test-selector="alphabetNavigationItemLetter"
                        id="alphabetNavigation"
                    >
                        <Link
                            {...styles.navigationLink}
                            onClick={() => handleClickOfLetter(alphabetItem.letter)}
                            disabled={!alphabetItem.linkable}
                        >
                            {alphabetItem.letter}
                        </Link>
                    </StyledSpan>
                ))}
            </StyledWrapper>
            <StyledWrapper {...styles.mobileContainer} data-test-selector="alphabetNavigation">
                <Select onChange={event => handleClickOfLetter(event.currentTarget.value)} {...styles.navigationSelect}>
                    <option value="">{translate("All Manufacturers")}</option>
                    {alphabet.map(
                        alphabetItem =>
                            alphabetItem.linkable && (
                                <option value={alphabetItem.letter} key={alphabetItem.letter}>
                                    {alphabetItem.letter}
                                </option>
                            ),
                    )}
                </Select>
            </StyledWrapper>
        </>
    );
};

export default AlphabetNavigation;
